import type { TableColumns } from '@ohpen/enso-ui';
import { Address, FirmType, CasePermission, FieldType, DraftFirmOperation, OperationType } from '@pmp/api';

export type AddressWithRowNumber = Address & {
  id?: string | null;
  rowNumber: number;
  isActive?: boolean | null;
  region?: string | null;
  businessDevelopmentManager?: string | null;
};

// Extended type: replaces tradingAddressIds with tradingAddresses (Array<Address>)
export type LenderFirmCRDataRow = {
  rowNumber: number;
  id?: string | null;
  name: string;
  type: FirmType;
  frn?: number | null;
  principalFirmIds: Array<string>;
  packagerFirmIds?: Array<string>;
  defaultPaymentRouteFirmId?: string | null;
  paymentRouteFirmIds?: Array<string> | null;
  operationsWhitelist?: Array<DraftFirmOperation> | null;
  btl?: CasePermission | null;
  rbtl?: CasePermission | null;
  rmc?: CasePermission | null;
  idsOfExclusivitySets?: Array<string> | null;
  isActive?: boolean | null;
  nacfbNumber?: string | null;
  reference?: string | null;
  qualifiedDomains?: Array<string> | null;
  qualifiedDomainRequiredForSelfRegistration?: boolean | null;
  email?: string | null;
  mobilePhone?: string | null;
  workPhone?: string | null;
  website?: string | null;
  complaintsWebsite?: string | null;
  sendCaseUpdatesToEmailAllowed?: boolean | null;
  firmAddress?: Address | null;
  firmAddressNumberOrBuilding?: string | null;
  firmAddressCity?: string | null;
  firmAddressPostCode?: string | null;
  firmAddressCountry?: string | null;
  firmAddressLine1?: string | null;
  firmAddressLine2?: string | null;
  firmAddressLine3?: string | null;
  firmAddressLine4?: string | null;
  firmAddressLine5?: string | null;
  tradingAddresses?: Array<AddressWithRowNumber> | null;
  lockedByActionId?: string | null;
};

export type TradingAddressRow = Omit<Address, 'lineItems'> & {
  id?: string | null;
  rowNumber: number;
  firmRowNumber: number;
  firmName: string;
  isActive?: boolean | null;
  region?: string | null;
  businessDevelopmentManager?: string | null;
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  line4?: string | null;
  line5?: string | null;
};

export type OperationWhitelistRow = {
  rowNumber: number;
  firmRowNumber: number;
  firmName: string;
  type?: OperationType | null;
  accountName?: string | null;
  accountNumber?: string | null;
  sortCode?: string | null;
  bankName?: string | null;
};

export const buildOperationsWhitelistRows = (firms: LenderFirmCRDataRow[]): OperationWhitelistRow[] => {
  let rowNum = 0;
  return firms.flatMap(firm =>
    (firm.operationsWhitelist ?? []).map(op => ({
      rowNumber: rowNum++,
      firmRowNumber: firm.rowNumber,
      firmName: firm.name,
      type: op.type,
      accountName: op.bankAccountDetails?.accountName ?? null,
      accountNumber: op.bankAccountDetails?.accountNumber ?? null,
      sortCode: op.bankAccountDetails?.sortCode ?? null,
      bankName: op.bankAccountDetails?.bankName ?? null,
    })),
  );
};

export const operationsWhitelistCRColumns: TableColumns<OperationWhitelistRow> = [
  {
    title: 'lender.firms.labels.operationType',
    dataIndex: 'type',
    key: 'type',
    width: 160,
    options: {
      format: { type: 'valueList' },
      filter: 'select',
      sorter: true,
      listName: FieldType.OPERATION_TYPE,
    },
  },
  {
    title: 'lender.firms.labels.accountName',
    dataIndex: 'accountName',
    key: 'accountName',
    width: 200,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.accountNumber',
    dataIndex: 'accountNumber',
    key: 'accountNumber',
    width: 180,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.sortCode',
    dataIndex: 'sortCode',
    key: 'sortCode',
    width: 120,
    options: { sorter: true, filter: 'search' },
  },
];

export const buildTradingAddressRows = (firms: LenderFirmCRDataRow[]): TradingAddressRow[] => {
  let rowNum = 0;
  return firms.flatMap(firm =>
    (firm.tradingAddresses ?? []).map(addr => {
      const { lineItems, ...addrRest } = addr;
      return {
        ...addrRest,
        id: addrRest.id ?? null,
        name: addrRest.name ?? null,
        numberOrBuilding: addrRest.numberOrBuilding ?? null,
        city: addrRest.city ?? null,
        postCode: addrRest.postCode ?? null,
        country: addrRest.country ?? null,
        isActive: addrRest.isActive ?? null,
        region: addrRest.region ?? null,
        businessDevelopmentManager: addrRest.businessDevelopmentManager ?? null,
        rowNumber: rowNum++,
        firmRowNumber: firm.rowNumber,
        firmName: firm.name,
        line1: lineItems?.[0] ?? null,
        line2: lineItems?.[1] ?? null,
        line3: lineItems?.[2] ?? null,
        line4: lineItems?.[3] ?? null,
        line5: lineItems?.[4] ?? null,
      };
    }),
  );
};

export const tradingAddressCRColumns: TableColumns<TradingAddressRow> = [
  {
    title: 'lender.firms.labels.tradingName',
    dataIndex: 'name',
    key: 'name',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.isActive',
    dataIndex: 'isActive',
    key: 'isActive',
    width: 100,
    options: {
      format: { type: 'boolean' },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
  {
    title: 'lender.firms.labels.addressLine1',
    dataIndex: 'line1',
    key: 'line1',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.addressLine2',
    dataIndex: 'line2',
    key: 'line2',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.addressLine3',
    dataIndex: 'line3',
    key: 'line3',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.addressLine4',
    dataIndex: 'line4',
    key: 'line4',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.town',
    dataIndex: 'city',
    key: 'city',
    width: 140,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.county',
    dataIndex: 'line5',
    key: 'line5',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.postCode',
    dataIndex: 'postCode',
    key: 'postCode',
    width: 120,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.country',
    dataIndex: 'country',
    key: 'country',
    width: 140,
    options: { sorter: true, filter: 'search' },
  },
  {
    // Read-only like BDM below: only the firm form sets regionId; a free-text edit here would
    // change the display name only.
    title: 'lender.firms.labels.region',
    dataIndex: 'region',
    key: 'region',
    width: 160,
    options: { sorter: true, filter: 'search', readOnly: true },
  },
  {
    title: 'lender.firms.labels.businessDevelopmentManager',
    dataIndex: 'businessDevelopmentManager',
    key: 'businessDevelopmentManager',
    width: 200,
    // Read-only here: the BDM must be picked from the region-filtered dropdown in the firm form so
    // bdmId is set. A free-text edit would only set the display name, which the BRS write ignores.
    options: { sorter: true, filter: 'search', readOnly: true },
  },
];

export const lenderFirmCRColumns: TableColumns<LenderFirmCRDataRow> = [
  {
    title: 'lender.firms.labels.firmId',
    dataIndex: 'id',
    key: 'id',
    width: 200,
    options: {
      sorter: true,
      hide: true,
    },
  },
  {
    title: 'lender.firms.labels.name',
    dataIndex: 'name',
    key: 'name',
    width: 200,
    options: {
      sorter: true,
    },
  },
  {
    title: 'lender.firms.labels.type',
    dataIndex: 'type',
    key: 'type',
    width: 160,
    options: {
      format: {
        type: 'valueList',
      },
      filter: 'select',
      sorter: true,
      listName: FieldType.FIRM_TYPE,
    },
  },
  {
    title: 'lender.firms.labels.frn',
    dataIndex: 'frn',
    key: 'frn',
    width: 120,
    options: {
      sorter: true,
    },
  },
  {
    title: 'lender.firms.labels.reference',
    dataIndex: 'reference',
    key: 'reference',
    width: 120,
    options: {
      sorter: true,
    },
  },
  {
    title: 'lender.firms.labels.nacfbNumber',
    dataIndex: 'nacfbNumber',
    key: 'nacfbNumber',
    width: 120,
    options: {
      sorter: true,
    },
  },
  {
    title: 'lender.firms.labels.isActive',
    dataIndex: 'isActive',
    key: 'isActive',
    width: 100,
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
  {
    title: 'lender.firms.labels.btl',
    dataIndex: 'btl',
    key: 'btl',
    width: 100,
    options: {
      format: { type: 'valueList' },
      filter: 'select',
      listName: FieldType.CASE_PERMISSION_TYPE,
    },
  },
  {
    title: 'lender.firms.labels.rbtl',
    dataIndex: 'rbtl',
    key: 'rbtl',
    width: 100,
    options: {
      format: { type: 'valueList' },
      filter: 'select',
      listName: FieldType.CASE_PERMISSION_TYPE,
    },
  },
  {
    title: 'lender.firms.labels.rmc',
    dataIndex: 'rmc',
    key: 'rmc',
    width: 100,
    options: {
      format: { type: 'valueList' },
      filter: 'select',
      listName: FieldType.CASE_PERMISSION_TYPE,
    },
  },
  {
    title: 'lender.firms.labels.principalFirm',
    dataIndex: 'principalFirmIds',
    key: 'principalFirmIds',
    width: 200,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PRINCIPALS,
    },
  },
  {
    title: 'lender.firms.labels.packagerFirm',
    dataIndex: 'packagerFirmIds',
    key: 'packagerFirmIds',
    width: 200,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PACKAGERS,
    },
  },
  {
    title: 'lender.firms.labels.defaultPaymentRouteFirm',
    dataIndex: 'defaultPaymentRouteFirmId',
    key: 'defaultPaymentRouteFirmId',
    width: 200,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.FIRMS,
    },
  },
  {
    title: 'lender.firms.labels.paymentRouteFirm',
    dataIndex: 'paymentRouteFirmIds',
    key: 'paymentRouteFirmIds',
    width: 200,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.FIRMS,
    },
  },
  {
    title: 'lender.firms.labels.exclusivitySets',
    dataIndex: 'idsOfExclusivitySets',
    key: 'idsOfExclusivitySets',
    width: 200,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.EXCLUSIVITY_SETS,
    },
  },
  {
    title: 'lender.firms.labels.qualifiedDomains',
    dataIndex: 'qualifiedDomains',
    key: 'qualifiedDomains',
    width: 200,
    render: (value: string[] | null) => (value ?? []).join(', ') || '-',
    options: {
      sorter: true,
    },
  },
  {
    title: 'lender.lender.labels.numberOrBuilding',
    dataIndex: 'firmAddressNumberOrBuilding',
    key: 'firmAddressNumberOrBuilding',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.town',
    dataIndex: 'firmAddressCity',
    key: 'firmAddressCity',
    width: 140,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.postCode',
    dataIndex: 'firmAddressPostCode',
    key: 'firmAddressPostCode',
    width: 120,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.country',
    dataIndex: 'firmAddressCountry',
    key: 'firmAddressCountry',
    width: 140,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.lineItem1',
    dataIndex: 'firmAddressLine1',
    key: 'firmAddressLine1',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.lineItem2',
    dataIndex: 'firmAddressLine2',
    key: 'firmAddressLine2',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.lineItem3',
    dataIndex: 'firmAddressLine3',
    key: 'firmAddressLine3',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.lineItem4',
    dataIndex: 'firmAddressLine4',
    key: 'firmAddressLine4',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.lineItem5',
    dataIndex: 'firmAddressLine5',
    key: 'firmAddressLine5',
    width: 160,
    options: { sorter: true, filter: 'search' },
  },
  {
    title: 'lender.firms.labels.email',
    dataIndex: 'email',
    key: 'email',
    width: 200,
    options: {
      sorter: true,
    },
  },
  {
    title: 'lender.firms.labels.mobile',
    dataIndex: 'mobilePhone',
    key: 'mobilePhone',
    width: 160,
    options: {
      sorter: true,
    },
  },
  {
    title: 'lender.firms.labels.telephone',
    dataIndex: 'workPhone',
    key: 'workPhone',
    width: 160,
    options: {
      sorter: true,
    },
  },
  {
    title: 'lender.firms.labels.website',
    dataIndex: 'website',
    key: 'website',
    width: 200,
    options: {
      sorter: true,
    },
  },
  {
    title: 'lender.firms.labels.complaintsWebPage',
    dataIndex: 'complaintsWebsite',
    key: 'complaintsWebsite',
    width: 200,
    options: {
      sorter: true,
    },
  },
];
